import { Authenticator } from "@aws-amplify/ui-react";
import * as Sentry from "@sentry/react";
import { Amplify } from "aws-amplify";
import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";

import { createBrowserRouter, RouterProvider } from "react-router-dom";
import ErrorPage from "./global/errorPage.tsx";

import auth from "./auth/config.tsx";
import {
  authenticatorComponents,
  authenticatorFields,
} from "./auth/customization.tsx";

import logging from "./logging/config.tsx";

import "@aws-amplify/ui-react/styles.css";
import "./index.css";

import AppLayout from "./global/appLayout.tsx";

import { QueryClient, QueryClientProvider } from "react-query";
import NotFoundPage from "./global/notFoundPage.tsx";
import { useAuthentication } from "./hooks/useAuthentication.ts";
import AddCertificate from "./pages/certificates/addCertificate.tsx";
import CertificatesList from "./pages/certificates/certificatesList.tsx";
import Dashboard from "./pages/dashboard.tsx";
import EditEnrollee from "./pages/enrollees/editEnrollee.tsx";
import EnrolleeDetails from "./pages/enrollees/enrolleeDetails.tsx";
import EnrolleesList from "./pages/enrollees/enrolleesList.tsx";
import EditProject from "./pages/projects/editProject.tsx";
import ProjectDetails from "./pages/projects/projectDetails.tsx";
import ProjectsList from "./pages/projects/projectsList.tsx";
import AddRenewal from "./pages/renewals/addRenewal.tsx";
import AddReport from "./pages/reports/addReport.tsx";
import ReportsList from "./pages/reports/reportsList.tsx";
import EditSource from "./pages/sources/editSource.tsx";
import SourceDetails from "./pages/sources/sourceDetails.tsx";
import SourcesList from "./pages/sources/sourcesList.tsx";
import Test from "./pages/test.tsx";

Sentry.init(logging);

Amplify.configure({
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  Auth: auth,
});

const router = createBrowserRouter([
  {
    path: "/",
    element: <AppLayout />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: "/",
        element: <Dashboard />,
      },
      {
        path: "/sources",
        element: <SourcesList />,
      },
      {
        path: "/sources/:sourceId",
        element: <EditSource />,
      },
      {
        path: "/sources/:sourceId/details",
        element: <SourceDetails />,
      },
      {
        path: "/projects",
        element: <ProjectsList />,
      },
      {
        path: "/sources/:sourceId/projects/:projectId",
        element: <EditProject />,
      },
      {
        path: "/sources/:sourceId/projects/:projectId/details",
        element: <ProjectDetails />,
      },
      {
        path: "/enrollees",
        element: <EnrolleesList />,
      },
      {
        path: "/enrollees/new",
        element: <EditEnrollee requiresProjectSelection={true} />,
      },
      {
        path: "sources/:sourceId/projects/:projectId/enrollees/:enrolleeId/details",
        element: <EnrolleeDetails />,
      },
      {
        path: "sources/:sourceId/projects/:projectId/enrollees/:enrolleeId",
        element: <EditEnrollee requiresProjectSelection={false} />,
      },
      {
        path: "sources/:sourceId/projects/:projectId/enrollees/:enrolleeId/certificates/:certificateId",
        element: <AddCertificate />,
      },
      {
        path: "/certificates",
        element: <CertificatesList />,
      },
      {
        path: "sources/:sourceId/projects/:projectId/renewals/:renewalId",
        element: <AddRenewal />,
      },
      {
        path: "/reports",
        element: <ReportsList />,
      },
      {
        path: "/reports/new",
        element: <AddReport />,
      },
      {
        path: "/test",
        element: <Test />,
      },
      {
        path: "*",
        element: <NotFoundPage />,
      },
    ],
  },
]);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const LoggingProvider = ({ children }: { children: JSX.Element }) => {
  const { user } = useAuthentication();

  useEffect(() => {
    Sentry.setUser(
      user?.attributes?.email != null
        ? {
            email: user?.attributes?.email,
            name: user?.attributes?.name,
          }
        : null,
    );
  }, [user]);

  return <>{children}</>;
};

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <Authenticator
      hideSignUp={true}
      components={authenticatorComponents}
      formFields={authenticatorFields}
    >
      <LoggingProvider>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </LoggingProvider>
    </Authenticator>
  </React.StrictMode>,
);
